/**
 * Writes a one-sheet `.xlsx` workbook in the browser and downloads it, using SheetJS (`xlsx`).
 * Every "Download to Excel" in the store goes through {@link exportToExcel}:
 * - the product export of the cart, quote details and the shopping-list price view,
 *   `./Products/ProductsExportUtils.tsx` → `exportProductsToExcel`;
 * - the invoice list export, `./Invoices/InvoicesExporUtils.tsx` → `exportInvoicesToExcel`.
 *
 * Sheet layout: key / value metadata rows from the top in columns A and B, two empty rows, the
 * header row, then one row per data object. Column widths are measured in pixels with
 * `./ExcelExportUtils.tsx` → `computeTextWidth`, so this runs only in a browser.
 *
 * Related: `unit-tests/shared/AvailableStockDownloadData.spec.tsx` (mocks this function and checks
 * what the product export passes to it).
 */
import { utils, writeFile } from "xlsx";
import { computeTextWidth } from "./ExcelExportUtils";

/**
 * One metadata row. With `link`, the value cell is a hyperlink to that URL with `tooltipContent`
 * as its tooltip. `skipWidthCalculation` leaves the value out of column B's width; the product
 * export sets it on its terms-of-sale note, the longest value it writes.
 */
type KeyValueItem = {
	key: string;
	value: string;
	link?: string;
	tooltipContent?: string;
	skipWidthCalculation?: boolean;
};

/**
 * Builds the workbook and triggers the browser download of `<fileName>.xlsx`. `fileName` is also
 * the sheet name, so it must be a valid Excel sheet name (31 characters at most, no
 * `: \ / ? * [ ]`).
 *
 * Each data row is written in its objects' key order, not matched to `dataHeaders` by name: callers
 * must build every row with its keys in the same order as the headers. Does nothing (and logs to
 * the console) when `dataRows` is missing.
 */
export const exportToExcel = (metadata: KeyValueItem[], dataHeaders: string[], dataRows: any[], fileName: string) => {
	if (!dataRows) {
		console.error("No data to export");
		return;
	}

	// create workbook and sheet
	const workbook = utils.book_new();
	const sheet = utils.aoa_to_sheet([[]]); // create an empty sheet

	//add metadata to the sheet
	let metadataKeyWidth = 0;
	let metadataValueWidth = 0;
	metadata.forEach((item, index) => {
		const { key, value, link, tooltipContent, skipWidthCalculation } = item;
		let cellValue: string | any = value;
		metadataKeyWidth = Math.max(metadataKeyWidth, computeTextWidth(key));
		metadataValueWidth = Math.max(metadataValueWidth, computeTextWidth(value && !skipWidthCalculation ? value.toString() : ''));

		if(!!link) {
			cellValue = {
				s: { font: { color: { rgb: "0000FF" }, underline: true } },
				l: {
					Target: link,
					Tooltip: tooltipContent,
				},
				v: value
			};
		}

		utils.sheet_add_aoa(sheet, [[key, cellValue]], {
			origin: { r: index, c: 0 },
		});
	});


	//leave rows between metadata and data
	const datStartRow = Object.keys(metadata).length + 2;

	// setup column widths array
	// Columns A and B also hold the metadata, so they start at the metadata widths.
	const columnWidths: any[] = [metadataKeyWidth, metadataValueWidth];
	for(let i=2; i<dataHeaders.length; i++) columnWidths.push(0);

	// computing column widths based on data headers
	dataHeaders.forEach((title, index) => {
		columnWidths[index] = Math.max(columnWidths[index], computeTextWidth(title));
	});

	//setting  headers for excel
	utils.sheet_add_aoa(sheet, [dataHeaders], {
		origin: { r: datStartRow, c: 0 },
	});

	// add data
	const leftAlignment = { alignment: { horizontal: "left" } };
	dataRows.forEach((row, rowIndex) => {
		const rowData: {
			v: string;
			s: typeof leftAlignment;
		}[] = Object.keys(row).map((key, index) => {
			const value = row[key];
			columnWidths[index] = Math.max(columnWidths[index], computeTextWidth(value ? value.toString() : ''));
			return { v: value as string, s: leftAlignment };
		});
		utils.sheet_add_aoa(sheet, [rowData], {
			origin: { r: datStartRow + 1 + rowIndex, c: 0 },
		});
	});

	//append data and column widths to the sheet
	sheet["!cols"] = columnWidths.map((width) => ({ wpx: width }));
	utils.book_append_sheet(workbook, sheet, fileName);
	writeFile(workbook, `${fileName}.xlsx`);
};